import {Component, effect, inject, OnInit, signal} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import {TmtErpOutboundPanelFilterService} from '../../service/tmt-erp-outbound-panel-filter.service';
import {TmtErpOutboundReportGroupedService} from '../../service/tmt-erp-outbound-report-grouped.service';
import {TmtErpOutboundLookupService} from '../../service/tmt-erp-outbound-lookup.service';
import {ITmtErpOutboundItem, ITmtErpOutboundOrderGroup} from '../../interfaces/tmt-erp-outbound-panel.interface';
import {tmtErpOutboundResultChipStyle, isTmtErpOutboundError} from '../../constants/tmt-erp-outbound-panel.constants';
import {TmtErpOutboundRawMessageDialogComponent} from '../tmt-erp-outbound-raw-message-dialog/tmt-erp-outbound-raw-message-dialog.component';

@Component({
  selector: 'app-tmt-erp-outbound-report-grouped-list',
  standalone: false,
  templateUrl: './tmt-erp-outbound-report-grouped-list.component.html',
  styleUrl: './tmt-erp-outbound-report-grouped-list.component.scss',
})
export class TmtErpOutboundReportGroupedListComponent implements OnInit {
  private readonly filterService = inject(TmtErpOutboundPanelFilterService);
  private readonly groupedService = inject(TmtErpOutboundReportGroupedService);
  private readonly lookupService = inject(TmtErpOutboundLookupService);
  private readonly dialog = inject(MatDialog);

  protected readonly masterColumns = [
    'expand', 'pnr', 'erpOrderNumber', 'legalEntityName', 'actionCount', 'latestResult', 'latestDateCreated',
  ];

  protected readonly groups = signal<ITmtErpOutboundOrderGroup[]>([]);
  protected readonly loading = signal(false);
  protected readonly pageIndex = signal(0);
  protected readonly pageSize = signal(25);
  protected readonly totalItemCount = signal(0);

  private readonly legalEntityNames = signal<Map<number, string>>(new Map());

  private readonly expanded = new Set<string>();

  protected readonly chipStyle = tmtErpOutboundResultChipStyle;

  constructor() {
    effect(() => {
      const filters = this.filterService.filtersValue();
      if (!filters.ReloadGrid) return;
      this.pageIndex.set(0);
      this.load();
    });
  }

  ngOnInit(): void {
    this.lookupService.getLegalEntityNames().subscribe(names => this.legalEntityNames.set(names));
    this.load();
  }

  protected refresh(): void {
    this.load();
  }

  protected isExpanded(group: ITmtErpOutboundOrderGroup): boolean {
    return this.expanded.has(this.groupKey(group));
  }

  protected toggle(group: ITmtErpOutboundOrderGroup): void {
    const key = this.groupKey(group);
    if (this.expanded.has(key)) {
      this.expanded.delete(key);
    } else {
      this.expanded.add(key);
    }
  }

  protected onPage(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.load();
  }

  protected pnrLabel(group: ITmtErpOutboundOrderGroup): string {
    return group.pnr?.trim() ? group.pnr : '(no PNR)';
  }

  protected legalEntityName(group: ITmtErpOutboundOrderGroup): string {
    if (group.legalEntityId == null) return '';
    return this.legalEntityNames().get(group.legalEntityId) ?? String(group.legalEntityId);
  }

  protected isError(action: ITmtErpOutboundItem): boolean {
    return isTmtErpOutboundError(action.result, action.errorDescription);
  }

  protected openRawMessage(action: ITmtErpOutboundItem): void {
    if (!action.rawMessage) return;
    this.dialog.open(TmtErpOutboundRawMessageDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {title: 'Raw Message', content: action.rawMessage},
    });
  }

  protected openError(action: ITmtErpOutboundItem): void {
    this.dialog.open(TmtErpOutboundRawMessageDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {title: 'Error Description', content: action.errorDescription || 'No error description provided.'},
    });
  }

  private groupKey(group: ITmtErpOutboundOrderGroup): string {
    return group.pnr ?? '';
  }

  private load(): void {
    this.loading.set(true);
    this.groupedService.getGroups({pageIndex: this.pageIndex(), pageSize: this.pageSize()}).subscribe({
      next: (response) => {
        this.groups.set(response.items ?? []);
        this.pageIndex.set((response.pagination?.pageNumber ?? 1) - 1);
        this.totalItemCount.set(response.pagination?.totalItemCount ?? 0);
        this.loading.set(false);
      },
      error: () => {
        this.groups.set([]);
        this.totalItemCount.set(0);
        this.loading.set(false);
      },
    });
  }
}
